import { useState } from "react";
import { Modal } from "../components/Modal";
import { Pagination } from "../components/Pagination";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Table,
  Td,
  Th,
  TableSkeleton,
  Tag,
  cx,
} from "../components/ui";
import { Icon } from "../components/icons";
import {
  useDecideInferenceRequest,
  useInferenceCampaigns,
  useDecideInferenceRequests,
  useInferenceRequests,
} from "../lib/queries";
import { formatRelative } from "../lib/format";
import { money } from "../lib/money";
import { useSaveInferenceCampaign } from "../lib/queries";
import { InferenceCampaignForm } from "./InferenceCampaignForm";
import type { InferenceCampaign, InferenceDecisionBatch } from "../lib/types";

/**
 * The free-inference programme.
 *
 * Filed under Inference rather than Commerce, though it reports like a
 * promotion. The rail's rule is that a title predicts its contents, and the
 * work here is AI-shaped: the model list comes from the catalogue above this
 * page and the cost lands in the usage report below it. That is the same
 * one-motion argument that put those two together.
 *
 * Two surfaces, and the second is where the time goes. A campaign is configured
 * once; the queue is opened because somebody is waiting.
 */

const TABS = [
  { value: "requested", label: "Waiting" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
] as const;

/*
 * Both figures arrive in micros. They used to arrive in picounits, which is
 * what the column stores and the wrong unit to send over JSON: a $10,000
 * campaign is 1e16 picounits, past what a JSON number represents at all.
 */
/* One page of the queue. Fifty is also the batch endpoint's ceiling, so a
   full page is exactly one call. */
const PAGE = 50;

function spentOf(campaign: InferenceCampaign): number {
  /* A quota of zero is not reachable through the form, but a division by one
     renders as NaN% rather than failing, so it is worth not doing. */
  if (campaign.quotaMicros <= 0) return 0;
  return Math.round((campaign.consumedMicros / campaign.quotaMicros) * 100);
}

/**
 * The pool, as a bar rather than a number.
 *
 * A percentage tells an operator what it is; a bar tells them, across a page of
 * campaigns, which one needs attention without reading any of them. Colour is
 * state and nothing else -- the accent while there is room, amber as it closes,
 * and the danger tone once it is spent, which is the one row where every key
 * has already stopped.
 */
function PoolMeter({ percent }: { percent: number }) {
  const capped = Math.min(100, Math.max(0, percent));
  const tone = capped >= 100 ? "bg-danger" : capped >= 80 ? "bg-warn" : "bg-accent";

  return (
    <div
      className="h-1.5 w-full overflow-hidden rounded-full bg-line"
      role="img"
      aria-label={`${capped}% of the pool spent`}
    >
      <div
        className={cx("h-full rounded-full transition-[width] duration-500 ease-smooth", tone)}
        style={{ width: `${capped}%` }}
      />
    </div>
  );
}

/** 1 234 567 → "1.2M". Tokens are the figure this is advertised in. */
function compactTokens(n: number): string {
  if (n >= 1_000_000_000_000) return `${(n / 1_000_000_000_000).toFixed(1)}T`;
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export function InferenceCampaignsPage() {
  /* Sent per keystroke and paged from zero on each one, the way every other
     operator list here searches. */
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const campaigns = useInferenceCampaigns({ limit: PAGE, offset, q: q || undefined });
  const [openId, setOpenId] = useState<string | null>(null);
  const [editing, setEditing] = useState<InferenceCampaign | "new" | null>(null);

  const rows = campaigns.data?.data ?? [];
  const selected = rows.find((c) => c.id === openId) ?? rows[0] ?? null;

  return (
    <div className="space-y-8">
      <PageHeader
        title="Free inference"
        description="Keys an operator issues against a funded pool. A recipient is lent the campaign's credential, not given credit — nothing reaches their balance, and the key stops when the campaign does."
        action={<Button onClick={() => setEditing("new")}>New campaign</Button>}
      />

      {campaigns.error && <Alert>{campaigns.error.message}</Alert>}

      {/* Hidden until there is more than one page's worth to look through: a
          search box over three rows is furniture. */}
      {(q || (campaigns.data?.total ?? 0) > PAGE) && (
        <input
          type="search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOffset(0);
          }}
          placeholder="Find a campaign by name or code"
          aria-label="Search campaigns"
          className="h-9 w-full border border-line bg-canvas px-3 text-sm text-fg placeholder:text-fg-subtle sm:max-w-xs"
        />
      )}

      {campaigns.isPending ? (
        <TableSkeleton columns={6} />
      ) : rows.length === 0 ? (
        q ? (
          <EmptyState
            title={`Nothing matches “${q}”`}
            description="Campaigns are searched by name and by code."
            action={<Button onClick={() => setQ("")}>Clear the search</Button>}
          />
        ) : (
          <EmptyState
            title="No campaigns yet"
            description="A campaign needs an organization to fund it, a model list, a per-key monthly allowance and a quota. Keys are issued from the queue once somebody asks."
            action={<Button onClick={() => setEditing("new")}>Create one</Button>}
          />
        )
      ) : (
        <Table
          head={
            <tr>
              <Th>Campaign</Th>
              <Th priority="md">Models</Th>
              <Th numeric>Pool spent</Th>
              <Th numeric priority="lg">Keys</Th>
              <Th numeric priority="lg">Converted</Th>
              <Th numeric>Waiting</Th>
              <Th priority="sm" />
            </tr>
          }
        >
          {rows.map((c) => {
            const spent = spentOf(c);
            const open = selected?.id === c.id;
            return (
              <tr
                key={c.id}
                onClick={() => setOpenId(c.id)}
                /*
                  Selecting a row drives the queue below it, which is offscreen
                  on a short viewport — so a tint alone leaves somebody
                  wondering whether the click did anything. The left edge is the
                  same device the plan table uses for its recommended row, and
                  it is reserved transparently on every row so selecting one
                  does not nudge its text two pixels out of the column.
                */
                className={cx(
                  "cursor-pointer border-l-2 transition-colors duration-200 ease-smooth",
                  open
                    ? "border-l-accent bg-accent-soft/40"
                    : "border-l-transparent hover:bg-surface",
                )}
              >
                <Td>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-fg">{c.name}</span>
                    {!c.active && <Tag>off</Tag>}
                    {c.consumedMicros >= c.quotaMicros && <Tag tone="bad">quota spent</Tag>}
                    {c.maxKeys !== null && c.keysIssued >= c.maxKeys && <Tag tone="warn">full</Tag>}
                  </div>
                  <div className="ident mt-0.5 text-[11px] text-fg-subtle">{c.code}</div>
                </Td>
                <Td priority="md">
                  <div className="text-fg-muted">
                    {c.allowedModels.length === 1
                      ? c.allowedModels[0]
                      : `${c.allowedModels.length} models`}
                  </div>
                  <div className="text-[11px] text-fg-subtle">
                    {money(c.keyMonthlyLimitMicros)} per key each month
                  </div>
                </Td>
                <Td numeric>
                  {/*
                    Cost is the guard and tokens are the story, so both are
                    shown — the bar is what an operator scans, and the token
                    figure is the one the campaign was advertised in.
                  */}
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-16">
                      <PoolMeter percent={spent} />
                    </div>
                    <span className="w-9 text-right tabular-nums text-fg">{spent}%</span>
                  </div>
                  <div className="mt-1 text-[11px] text-fg-subtle tabular-nums">
                    {compactTokens(c.tokensUsed)} tokens ·{" "}
                    {money(c.consumedMicros)}
                  </div>
                </Td>
                <Td numeric priority="lg">
                  <span className="tabular-nums text-fg">{c.keysIssued}</span>
                  {c.maxKeys !== null && (
                    <span className="text-fg-subtle tabular-nums"> / {c.maxKeys}</span>
                  )}
                </Td>
                <Td numeric priority="lg">
                  {/* The only column that says whether the programme worked:
                      holders who went on to pay for something themselves. */}
                  <span className="tabular-nums text-fg">{c.convertedCount}</span>
                  <span className="text-fg-subtle tabular-nums">
                    {" "}
                    / {c.approvedCount}
                  </span>
                </Td>
                <Td numeric>
                  <span
                    className={cx(
                      "tabular-nums",
                      c.pendingCount > 0 ? "text-accent" : "text-fg-subtle",
                    )}
                  >
                    {c.pendingCount}
                  </span>
                </Td>
                <Td numeric priority="sm">
                  {/* Both stop propagation: acting on a row must not also
                      select it and jump the queue below under the modal. */}
                  <ActiveToggle campaign={c} />
                  <button
                    type="button"
                    className="ml-3 text-xs text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditing(c);
                    }}
                  >
                    Edit
                  </button>
                </Td>
              </tr>
            );
          })}
        </Table>
      )}

      <Pagination
        total={campaigns.data?.total}
        limit={PAGE}
        offset={offset}
        onChange={(next) => {
          setOffset(next);
          /* The queue below follows whichever campaign is selected, and the
             selection falls back to the first row. Clearing it on a page change
             stops the queue showing a campaign that is no longer on screen. */
          setOpenId(null);
        }}
      />

      {selected && <RequestQueue campaign={selected} />}

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === "new" ? "New campaign" : "Edit campaign"}
        description={
          editing === "new"
            ? "Saved switched off. Nothing is spent until you turn it on."
            : "Changes apply to keys already issued, the model list included."
        }
      >
        {editing && (
          <InferenceCampaignForm
            campaign={editing === "new" ? undefined : editing}
            onDone={() => setEditing(null)}
          />
        )}
      </Modal>
    </div>
  );
}

/**
 * The switch that starts and stops the spending.
 *
 * On the row rather than inside the edit form, because it is the one control an
 * operator reaches for without wanting to change anything else -- ending a
 * campaign should not mean opening a budget form and finding the right field.
 */
function ActiveToggle({ campaign }: { campaign: InferenceCampaign }) {
  const save = useSaveInferenceCampaign(campaign.id);

  return (
    <button
      type="button"
      disabled={save.isPending}
      onClick={(e) => {
        e.stopPropagation();
        save.mutate({ active: !campaign.active });
      }}
      className={cx(
        "text-xs transition-colors duration-200 ease-smooth disabled:text-fg-subtle",
        campaign.active ? "text-fg-muted hover:text-danger" : "text-accent hover:text-accent-hover",
      )}
    >
      {save.isPending ? "…" : campaign.active ? "Turn off" : "Turn on"}
    </button>
  );
}

function RequestQueue({ campaign }: { campaign: InferenceCampaign }) {
  const [status, setStatus] = useState<string>("requested");
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState("");
  const requests = useInferenceRequests(campaign.id, {
    status,
    limit: PAGE,
    offset,
    q: q || undefined,
  });
  const decide = useDecideInferenceRequest(campaign.id);
  const decideMany = useDecideInferenceRequests(campaign.id);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [note, setNote] = useState("");

  /*
   * Selection, and the three things it has to survive.
   *
   * A Set of ids rather than a flag per row, because the rows are replaced on
   * every refetch and a flag on the row object would be lost with them. Cleared
   * when the tab or the page changes, because a selection that spans pages is
   * one an operator cannot see the whole of — and this button sends email.
   */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulk, setBulk] = useState<null | "approve" | "decline">(null);
  const [bulkNote, setBulkNote] = useState("");
  const [outcome, setOutcome] = useState<InferenceDecisionBatch | null>(null);

  const rows = requests.data?.data ?? [];
  const total = requests.data?.total;

  /* Only a waiting request can be decided, so only those can be picked. */
  const selectable = rows.filter((r) => r.status === "requested");
  const chosen = selectable.filter((r) => selected.has(r.id));
  const allChosen = selectable.length > 0 && chosen.length === selectable.length;

  function reset(next: () => void) {
    setSelected(new Set());
    setBulk(null);
    setBulkNote("");
    setOutcome(null);
    setRejecting(null);
    setConfirming(null);
    next();
  }

  function toggle(id: string) {
    setOutcome(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /*
   * How many more keys this campaign can issue, when it says.
   *
   * Shown next to the count being approved because the two decide each other:
   * selecting thirty against room for five sends five keys and refuses
   * twenty-five, and an operator should meet that number before pressing the
   * button rather than in the result.
   */
  const room =
    campaign.maxKeys === null ? null : Math.max(0, campaign.maxKeys - campaign.keysIssued);
  const overRoom = room !== null && chosen.length > room;

  const runBulk = (decision: "approved" | "rejected") =>
    decideMany.mutate(
      {
        requestIds: chosen.map((r) => r.id),
        decision,
        ...(decision === "rejected" ? { note: bulkNote.trim() } : {}),
      },
      {
        onSuccess: (result) => {
          setOutcome(result.data);
          setSelected(new Set());
          setBulk(null);
          setBulkNote("");
        },
      },
    );

  return (
    <Card>
      <div className="border-b border-line p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-fg">Requests · {campaign.name}</h2>
            <p className="mt-1 max-w-prose text-sm text-fg-muted">
              Approving issues the key and emails it. It is sent once and cannot be re-sent — to
              replace one, revoke it and approve again.
            </p>
          </div>
          {/* The capacity an approval spends, where the approving happens. */}
          <div className="shrink-0 text-right text-sm">
            <p className="tabular-nums text-fg">
              {campaign.keysIssued}
              {campaign.maxKeys !== null && (
                <span className="text-fg-subtle"> / {campaign.maxKeys}</span>
              )}
            </p>
            <p className="text-[11px] text-fg-subtle">
              {room === null ? "keys issued · no cap" : `keys issued · room for ${room} more`}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-line p-3">
        {TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            aria-pressed={tab.value === status}
            onClick={() =>
              reset(() => {
                setStatus(tab.value);
                setOffset(0);
              })
            }
            className={cx(
              "flex h-9 items-center gap-2 border px-3 text-sm transition-colors duration-200 ease-smooth pointer-coarse:min-h-11",
              tab.value === status
                ? "border-accent/40 bg-accent-soft/50 text-fg"
                : "border-line text-fg-muted hover:border-line-strong hover:text-fg",
            )}
          >
            {tab.label}
            {tab.value === "requested" && campaign.pendingCount > 0 && (
              <span className="tabular-nums text-accent-hover">{campaign.pendingCount}</span>
            )}
          </button>
        ))}

        {/* Searched on the server, so it reaches past this page rather than
            filtering the fifty rows that happen to be loaded. */}
        <input
          type="search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            reset(() => setOffset(0));
          }}
          placeholder="Find an organization or applicant"
          aria-label="Search requests"
          className="ml-auto h-9 min-w-0 flex-1 border border-line bg-canvas px-3 text-sm text-fg placeholder:text-fg-subtle sm:max-w-xs"
        />
      </div>

      {(decide.error || decideMany.error) && (
        <div className="p-5 pb-0">
          <Alert>{(decide.error ?? decideMany.error)?.message}</Alert>
        </div>
      )}

      {/*
        What the last batch did, per outcome rather than as a count.

        "Fifteen failed" is not something an operator can act on: the reasons
        differ per row, and the rows that worked are not coming back. Named
        reasons, deduplicated, so a campaign that ran out says so once instead
        of fifteen times.
      */}
      {outcome && (
        <div className="p-5 pb-0">
          <Alert tone={outcome.failed === 0 ? "success" : "info"}>
            <p>
              {outcome.decided > 0 && (
                <>
                  <strong className="tabular-nums">{outcome.decided}</strong> decided
                  {outcome.failed > 0 && ", "}
                </>
              )}
              {outcome.failed > 0 && (
                <>
                  <strong className="tabular-nums">{outcome.failed}</strong> left alone
                </>
              )}
              .
            </p>
            {outcome.failed > 0 && (
              <ul className="mt-2 space-y-1 text-xs">
                {[...new Set(outcome.results.filter((r) => r.error).map((r) => r.error!))].map(
                  (reason) => (
                    <li key={reason}>· {reason}</li>
                  ),
                )}
              </ul>
            )}
          </Alert>
        </div>
      )}

      {/*
        The bar, present only when something is selected.

        Between the tabs and the list rather than pinned to the window: it
        belongs to this queue, and a floating bar over a page with two other
        cards on it is a control whose scope has to be guessed.
      */}
      {chosen.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 border-b border-line bg-raised px-5 py-3">
          <span className="text-sm text-fg">
            <strong className="tabular-nums">{chosen.length}</strong> selected
          </span>

          {bulk === null && (
            <>
              <div className="ml-auto flex gap-2">
                <Button variant="ghost" onClick={() => setSelected(new Set())}>
                  Clear
                </Button>
                <Button variant="secondary" onClick={() => setBulk("decline")}>
                  Decline all
                </Button>
                <Button onClick={() => setBulk("approve")}>Approve all</Button>
              </div>
            </>
          )}

          {/*
            Approving in bulk is a one-way door held open wider. The count is
            restated in the confirmation rather than only in the bar above it,
            because the bar is what somebody just changed and the confirmation
            is what they are agreeing to.
          */}
          {bulk === "approve" && (
            <div className="flex w-full flex-col gap-2">
              <p className="text-sm leading-6 text-fg-muted">
                Sends {chosen.length} {chosen.length === 1 ? "key" : "keys"}, one to each
                organization&rsquo;s owners and admins. They cannot be re-sent.
                {overRoom && (
                  <>
                    {" "}
                    <span className="text-warn">
                      This campaign has room for {room}, so {chosen.length - room!} will be left
                      waiting.
                    </span>
                  </>
                )}
              </p>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setBulk(null)}>
                  Cancel
                </Button>
                <Button loading={decideMany.isPending} onClick={() => runBulk("approved")}>
                  Send {chosen.length} {chosen.length === 1 ? "key" : "keys"}
                </Button>
              </div>
            </div>
          )}

          {bulk === "decline" && (
            <div className="flex w-full flex-col gap-2">
              <label htmlFor="bulk-note" className="text-sm text-fg">
                Why? All {chosen.length} are sent this same message.
              </label>
              <textarea
                id="bulk-note"
                value={bulkNote}
                onChange={(e) => setBulkNote(e.target.value)}
                rows={2}
                className="w-full border border-line bg-canvas p-2 text-sm text-fg"
                placeholder="This campaign is for accounts that have not used Maxlayer before."
              />
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setBulk(null)}>
                  Cancel
                </Button>
                <Button
                  variant="secondary"
                  disabled={bulkNote.trim().length === 0}
                  loading={decideMany.isPending}
                  onClick={() => runBulk("rejected")}
                >
                  Decline {chosen.length}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {requests.isPending ? (
        <div className="p-5">
          <TableSkeleton columns={3} rows={3} />
        </div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-fg-muted">
          {q
            ? `Nothing matches “${q}”.`
            : status === "requested"
              ? "Nobody is waiting."
              : "Nothing here yet."}
        </div>
      ) : (
        <>
          {/* Select-all covers this page and says so. A control that silently
              reached rows on a page nobody had looked at would be the wrong
              kind of convenience on a button that sends email. */}
          {selectable.length > 0 && (
            <label className="flex cursor-pointer items-center gap-3 border-b border-line px-5 py-2.5 text-sm text-fg-muted">
              <input
                type="checkbox"
                className="size-4 accent-accent"
                checked={allChosen}
                ref={(el) => {
                  if (el) el.indeterminate = chosen.length > 0 && !allChosen;
                }}
                onChange={() => {
                  setOutcome(null);
                  setSelected(
                    allChosen ? new Set() : new Set(selectable.map((r) => r.id)),
                  );
                }}
              />
              Select the {selectable.length} on this page
            </label>
          )}

          <ul className="grid-hairline">
            {rows.map((r) => (
              <li key={r.id} className={cx("p-5", selected.has(r.id) && "bg-raised")}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex min-w-0 gap-3">
                    {r.status === "requested" ? (
                      <input
                        type="checkbox"
                        aria-label={`Select ${r.orgName}`}
                        className="mt-1 size-4 shrink-0 accent-accent"
                        checked={selected.has(r.id)}
                        onChange={() => toggle(r.id)}
                      />
                    ) : (
                      <span className="w-4 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-fg">{r.orgName}</span>
                        {/* Eligibility is what an operator is judging, so the one
                            fact it turns on sits on the row rather than a click
                            away. */}
                        {r.hasPaid ? (
                          <Tag tone="warn">has paid before</Tag>
                        ) : (
                          <Tag tone="good">new</Tag>
                        )}
                      </div>
                      <p className="mt-0.5 text-[11px] text-fg-subtle">
                        {r.userName ?? "—"}
                        {r.userEmail && <span className="font-mono"> · {r.userEmail}</span>} · asked{" "}
                        {formatRelative(r.requestedAt)}
                        {r.decidedAt && ` · decided by ${r.decidedByName ?? "—"}`}
                      </p>
                      {r.decisionNote && (
                        <p className="mt-2 max-w-prose text-sm text-fg-muted">{r.decisionNote}</p>
                      )}
                    </div>
                  </div>

                  {r.status === "requested" && confirming !== r.id && (
                    <div className="flex shrink-0 gap-2">
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setRejecting(rejecting === r.id ? null : r.id);
                          setConfirming(null);
                          setNote("");
                        }}
                      >
                        Decline
                      </Button>
                      <Button
                        onClick={() => {
                          setConfirming(r.id);
                          setRejecting(null);
                        }}
                      >
                        Approve
                      </Button>
                    </div>
                  )}

                  {/*
                    Approving is a one-way door and does not look like one: it
                    mints a credential and mails it, and the token exists in that
                    message and nowhere else. There is no undo — only revoking
                    the key afterwards and issuing another — so the second click
                    is the whole of the safety here.
                  */}
                  {r.status === "requested" && confirming === r.id && (
                    <div className="flex shrink-0 flex-col items-end gap-2">
                      <p className="max-w-xs text-right text-xs leading-5 text-fg-muted">
                        Sends a key to {r.orgName}&rsquo;s owners and admins. It cannot be re-sent.
                      </p>
                      <div className="flex gap-2">
                        <Button variant="ghost" onClick={() => setConfirming(null)}>
                          Cancel
                        </Button>
                        <Button
                          loading={decide.isPending}
                          onClick={() =>
                            decide.mutate(
                              { requestId: r.id, decision: "approved" },
                              { onSuccess: () => setConfirming(null) },
                            )
                          }
                        >
                          Send the key
                        </Button>
                      </div>
                    </div>
                  )}
                  {r.status === "approved" && (
                    <span className="flex shrink-0 items-center gap-2 text-sm text-fg-subtle">
                      <Icon name="check" className="size-4" />
                      key issued
                    </span>
                  )}
                </div>

                {rejecting === r.id && (
                  <div className="mt-4 border-l-2 border-line pl-4">
                    <label htmlFor={`note-${r.id}`} className="text-sm text-fg">
                      Why? The applicant is sent this.
                    </label>
                    <textarea
                      id={`note-${r.id}`}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={2}
                      className="mt-2 w-full border border-line bg-canvas p-2 text-sm text-fg"
                      placeholder="This campaign is for accounts that have not used Maxlayer before."
                    />
                    <div className="mt-2 flex gap-2">
                      <Button
                        variant="secondary"
                        disabled={note.trim().length === 0}
                        loading={decide.isPending}
                        onClick={() =>
                          decide.mutate(
                            { requestId: r.id, decision: "rejected", note: note.trim() },
                            { onSuccess: () => setRejecting(null) },
                          )
                        }
                      >
                        Send decline
                      </Button>
                      <Button variant="ghost" onClick={() => setRejecting(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>

          {/* The queue was capped at fifty with nothing past it, which made a
              batch button useless the moment a campaign got busy. */}
          <Pagination
            total={total}
            limit={PAGE}
            offset={offset}
            onChange={(next) => reset(() => setOffset(next))}
          />
        </>
      )}
    </Card>
  );
}
